import React, {
  useMemo, useCallback, useEffect, useState,
} from 'react';
import {
  Col,
  Divider,
  Row,
  Spin,
  message,
  notification,
  Alert,
} from 'antd';
import { Table as TableV5 } from 'antd-v5';
import { PropTypes } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import { DateTime } from 'luxon';
import { isNullOrUndefined } from 'ontraccr-common/lib/Common';
import {
  InfoCircleTwoTone,
  ReloadOutlined,
  FilterFilled,
  FilterOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import ResizeObserver from 'rc-resize-observer';

import TimeTracking from '../../state/timeTracking';

import TimeCardDatePicker from '../../timecards/TimeCardDatePicker';

import OnTraccrButton from '../../common/buttons/OnTraccrButton';
import FilterDrawer from '../../common/filters/FilterDrawer';
import HoverHelp from '../../common/HoverHelp';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import OnTraccrSwitch from '../../common/inputs/OnTraccrSwitch';

import { CUSTOM_EXPORT_PROP_TYPE } from '../../settings/Exports/exports.constants';
import ReportExcelExport from '../reportExcelExport';
import Debouncer from '../../helpers/Debouncer';
import { request } from '../../helpers/requests';
import { getIdMap } from '../../helpers/helpers';
import { findPayrollStartAndEndDates } from '../../helpers/payroll';
import { isFilterActive } from '../../schedule/GanttSchedule/ganttScheduleHelpers';

import {
  constructAfterExportPayload,
  getBatchNumber,
  getDataKey,
  rowMatchesExportColCombinedFilter,
  rowMatchesExportNumberFilter,
  updateFormNumbers,
  isExportDateTimeColumn,
  isExportNumericLikeColumn,
  isExportTextOrAttributeColumn,
  formatCustomExportDateTimeCell,
  extractTextFromCell,
  rowMatchesExportDateFilter,
  applyExportTableFilters,
  getColumnSubTotal,
} from './exports.helpers';

import { LINE_NUMBER_COLUMN, PATH_MAP, DATE_FORMAT_MAP } from './exports.constants';

import FormDetailView from '../../forms/CompletedForms/FormDetailView';
import TimeCardSlider from '../../timecards/TimeCardSlider/TimeCardSlider';
import BoardCardDrawer from '../../boards/BoardCardDrawer';

import {
  openCardDrawer,
  getBoards,
  getBoardCardTemplates,
} from '../../boards/state/boards.actions';
import { getFormById } from '../../forms/state/forms.actions';
import { getTimecardHistory } from '../../timecards/state/timecards.actions';
import { getFieldMap } from '../../forms/formHelpers';

import CustomExportFilterTab from './CustomExportFilterTab';
import {
  renderCustomExportDateTimeFilterDropdown,
  renderCustomExportGeneralFilterDropdown,
  renderCustomExportNumberFilterDropdown,
  renderCustomExportTextFilterDropdown,
} from './renderCustomExportFilterDropdowns';
import { getUserCustomFieldTemplate } from '../../users/state/users.actions';
import { sortByObjProperty } from '../../common/helpers';

const exporter = new ReportExcelExport();

const debouncers = {};

const SUBTOTAL_LABEL_KEY = 'subtotal-label';


const DEFAULT_EXPORT_COLUMNS = [];
function CustomExportsTable({
  selectedExport = {},
  scroll,
  onHeaderResize,
  canFilter,
}) {
  const dispatch = useDispatch();

  const timeEntryUserMap = useSelector((state) => state.timeTracking.timeEntryUserMap);
  const timeCardHistory = useSelector((state) => state.timecards.history);
  const firstPayrollDay = useSelector((state) => state.timecards.firstPayrollDay);
  const filterViews = useSelector((state) => state.reports.customExportFilters);
  const boards = useSelector((state) => state.boards.boards);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);

  const {
    settings: {
      payPeriod,
      semiMonthlyPayPeriodDates,
    } = {},
  } = useSelector((state) => state.settings.company ?? {});

  const [hideExported, setHideExported] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [excluded, setExcluded] = useState(0);
  const [showFormDetail, setShowFormDetail] = useState(false);
  const [showTimeDetail, setShowTimeDetail] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const [dateRange, setDateRange] = useState(null);
  const [calendarType, setCalendarType] = useState('payPeriod');
  const [shouldRefresh, setShouldRefresh] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [filters, setFilters] = useState();
  const [tableFilters, setTableFilters] = useState({});
  const [filteredData, setFilteredData] = useState([]);

  const users = useSelector((state) => state.users.users);
  const formTemplates = useSelector((state) => state.forms.templates);
  const customFields = useSelector((state) => state.timecards.customFields);
  const userCustomFieldTemplate = useSelector((state) => state.users.customFieldTemplate);

  const relevantFilterViews = useMemo(() => (
    filterViews.filter((f) => f.exportId === selectedExport?.id)
  ), [filterViews, selectedExport]);
  const filterIsActive = useMemo(() => isFilterActive(filters ?? {}), [filters]);
  const userMap = useMemo(() => getIdMap(users), [users]);

  const {
    id: exportId,
    title: exportTitle,
    columns: exportColumns = DEFAULT_EXPORT_COLUMNS,
    addLineNumbers = false,
    generateBatchNumber = false,
    generateFormNumber = false,
    summarizeEmployees = false,
    summarizeDays = false,
    summarizeProject = false,
    includeZeroHours = false,
    approvedOnly = false,
    divisionId = null,
    formTemplateId = null,
    dateFilterFieldId = 'lastUpdated',
    type,
    requiredApprovalStepId = null,
    boardId,
  } = selectedExport || {};

  const ourBoard = boards[boardId];
  const cardType = cardTemplates[ourBoard?.cardTypeId];

  const exportColumnFieldMap = useMemo(() => {
    const sections = [];
    if (type === 'forms' && formTemplates?.[formTemplateId]?.jsonFormData) {
      try {
        const parsed = JSON.parse(formTemplates[formTemplateId].jsonFormData);
        parsed?.sections?.forEach((s) => sections.push(s));
      } catch {
        // Fail silently
      }
    }
    if (type === 'boardCards' && cardType) {
      cardType.fields?.forEach((s) => sections.push(s));
    }
    if (type === 'timeEntries' && divisionId && customFields?.[divisionId]?.fields?.length) {
      customFields[divisionId].fields.forEach((s) => sections.push(s));
    }
    if (type === 'timeEntries' && userCustomFieldTemplate?.sections?.length) {
      userCustomFieldTemplate.sections.forEach((s) => sections.push(s));
    }
    return getFieldMap(sections, 'id');
  }, [
    type,
    formTemplateId,
    formTemplates,
    cardType,
    divisionId,
    customFields,
    userCustomFieldTemplate,
  ]);

  const canClick = formTemplateId || (!summarizeEmployees && !summarizeDays && !summarizeProject);

  // Allows only detecting field changes to avoid re-rendering the table when renaming columns
  const fieldIds = useMemo(() => {
    const ids = [];

    exportColumns
      .forEach(({ field, fields = [] }) => {
        if (field) {
          ids.push(field);
        } else {
          ids.push(fields.join(':'));
        }
      });

    return ids
      .sort()
      .join(', ');
  }, [exportColumns]);

  const specialColumns = useMemo(() => {
    if (!exportId) return [];
    const innerSpecialColumns = [];
    if (addLineNumbers) innerSpecialColumns.push(LINE_NUMBER_COLUMN);
    if (type === 'boardCards') return innerSpecialColumns;
    innerSpecialColumns.push({
      title: '',
      render: (_1, record) => {
        const { exportIds = [], batchNumber } = record ?? {};
        if (!exportIds?.includes(exportId)) return null;
        const suffix = batchNumber ? ` in batch ${batchNumber}` : '';
        let recordType;
        switch (type) {
          case 'forms':
            recordType = 'form';
            break;
          case 'boardCards':
            recordType = 'board card';
            break;
          default:
            recordType = 'time card';
        }
        return (
          <HoverHelp
            content={`This ${recordType} has already been exported${suffix}`}
            Icon={InfoCircleTwoTone}
            iconProps={{ twoToneColor: '#fdb81b' }}
          />
        );
      },
      width: 50,
    });

    return innerSpecialColumns;
  }, [addLineNumbers, exportId, type]);

  const formatRow = useCallback((row) => {
    const newRow = { ...row };
    exportColumns.forEach((col) => {
      if (col.field) {
        const raw = row[col.field];
        if (col.isCustomDateField) {
          const formatted = formatCustomExportDateTimeCell(
            raw,
            col,
            exportColumnFieldMap,
          );
          newRow[getDataKey(col)] = formatted ?? raw;
        }
      }
    });
    return newRow;
  }, [exportColumns, exportColumnFieldMap]);

  useEffect(() => {
    if (!dateRange) return () => 1;
    const fetchExportData = async () => {
      setLoading(true);
      const [startDate, endDate] = dateRange ?? [];
      const path = PATH_MAP[type];
      // Build base params that all export types need
      const baseParams = {
        exportId,
        divisionId,
        columns: exportColumns,
      };

      // Add type-specific parameters
      switch (type) {
        case 'forms': {
          baseParams.startTime = startDate.toMillis();
          baseParams.endTime = endDate.toMillis();
          baseParams.timezone = DateTime.local().zoneName;
          baseParams.approvedOnly = approvedOnly;
          baseParams.dateFilterFieldId = dateFilterFieldId;
          baseParams.formTemplateId = formTemplateId;
          if (requiredApprovalStepId) baseParams.requiredApprovalStepId = requiredApprovalStepId;
          break;
        }
        case 'timeEntries': {
          baseParams.startTime = startDate.toMillis();
          baseParams.endTime = endDate.toMillis();
          baseParams.timezone = DateTime.local().zoneName;
          baseParams.approvedOnly = approvedOnly;
          baseParams.dateFilterFieldId = dateFilterFieldId;
          baseParams.summarizeEmployees = summarizeEmployees;
          baseParams.summarizeDays = summarizeDays;
          baseParams.summarizeProject = summarizeProject;
          baseParams.includeZeroHours = includeZeroHours;
          break;
        }
        case 'boardCards': {
          baseParams.startTime = startDate.toMillis();
          baseParams.endTime = endDate.toMillis();
          baseParams.timezone = DateTime.local().zoneName;
          baseParams.boardId = selectedExport?.boardId;
          baseParams.dateFilterFieldId = dateFilterFieldId;
          break;
        }
        default: {
          break;
        }
      }

      const params = {
        params: baseParams,
      };
      if (filters) params.params.filters = filters;
      try {
        const { data: resp = [] } = await axios.get(path, params);
        const exportData = Array.isArray(resp) ? resp : (resp.data ?? []);
        const excludedCount = Array.isArray(resp) ? 0 : (resp.excluded ?? 0);
        setExcluded(excludedCount);
        if (!Array.isArray(exportData)) {
          Sentry.withScope(() => {
            Sentry.captureException(
              new Error('Invalid export data received from route'),
              { path, params, exportData },
            );
          });
          setRows([]);
        } else {
          setRows(exportData);
        }
      } catch (err) {
        const isTooLarge = err.response?.status === 413;
        if (isTooLarge) {
          notification.warn({
            message: 'Export Too Large',
            description: (
              <div>
                The export file exceeds the maximum allowable size for in-app generation.
                To retrieve your file, please submit a support ticket for assistance.
              </div>
            ),
          });
        } else {
          message.error('Failed to load export data');
          Sentry.withScope(() => {
            Sentry.captureException(err);
          });
        }
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    if (!(exportId in debouncers)) {
      debouncers[exportId] = new Debouncer();
    }
    if (exportColumns.length > 0) debouncers[exportId].debounce(fetchExportData, 500);
    setShouldRefresh(false);
    return () => {
      debouncers[exportId]?.clear();
    };
  }, [
    exportColumns,
    fieldIds,
    exportId,
    formTemplateId,
    approvedOnly,
    summarizeEmployees,
    summarizeDays,
    summarizeProject,
    includeZeroHours,
    divisionId,
    dateRange,
    type,
    shouldRefresh,
    filters,
    dateFilterFieldId,
    requiredApprovalStepId,
    formatRow,
  ]);

  const formattedRows = useMemo(
    () => rows.map(formatRow),
    [rows, formatRow],
  );

  const exportRows = useMemo(() => {
    if (!hideExported || type === 'boardCards') return formattedRows;
    const filtered = formattedRows.filter(({ exportIds }) => !exportIds.includes(exportId));
    return filtered;
  }, [formattedRows, hideExported, type, exportId]);

  // Gets filter options for each column
  const columnFilterOptionMap = useMemo(() => {
    const map = {};
    exportColumns.forEach((col) => {
      if (isExportDateTimeColumn(col)) return;
      if (isExportNumericLikeColumn(col, exportColumnFieldMap)) return;
      if (isExportTextOrAttributeColumn(col, exportColumnFieldMap)) return;

      const key = getDataKey(col);
      const unique = new Map();
      exportRows.forEach((row) => {
        const cellStr = extractTextFromCell(row[key]);
        const cellStrSplit = cellStr ? cellStr.split?.(', ') : [];

        cellStrSplit?.forEach((cell) => {
          if (!unique.has(cell)) {
            unique.set(cell, {
              value: cell,
              text: cell === '' ? '(empty)' : cell,
            });
          }
        });
      });
      map[key] = Array.from(unique.values()).sort(sortByObjProperty('text'));
    });
    return map;
  }, [exportRows, exportColumns, fieldIds, exportColumnFieldMap]);

  const visibleRowsForExport = useMemo(
    () => applyExportTableFilters({
      rows: exportRows,
      tableFilters,
      filteredData,
    }),
    [
      exportRows,
      tableFilters,
      filteredData,
    ],
  );

  const onTableChange = useCallback((_pagination, newFilters, _sorter, extra) => {
    setTableFilters(newFilters || {});
    setFilteredData(extra.currentDataSource);
  }, []);

  const hasSubTotals = useMemo(
    () => exportColumns.some((c) => c.subTotal),
    [exportColumns],
  );

  const subTotalsByKey = useMemo(() => {
    if (!hasSubTotals) return {};
    const map = {};
    exportColumns.forEach((col) => {
      if (!col.subTotal) return;
      map[getDataKey(col)] = getColumnSubTotal(col, visibleRowsForExport, exportColumnFieldMap);
    });
    return map;
  }, [exportColumns, visibleRowsForExport, hasSubTotals, exportColumnFieldMap]);

  const columns = useMemo(() => {
    if (!exportId) return [];

    const parsedColumns = [...specialColumns];

    if (hasSubTotals) {
      parsedColumns.unshift({
        key: SUBTOTAL_LABEL_KEY,
        title: '',
        dataIndex: SUBTOTAL_LABEL_KEY,
        width: 100,
        render: () => null,
      });
    }

    exportColumns.forEach((col) => {
      const dataKey = getDataKey(col);
      const titleLabel = typeof col.title === 'string' ? col.title : 'Column';
      const titleNode = (
        <div
          style={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          title={titleLabel}
        >
          {col.title}
        </div>
      );
      const columnFilteredValue = tableFilters[dataKey] ?? null;
      const baseColumnProps = {
        key: dataKey,
        title: titleNode,
        dataIndex: dataKey,
        width: 200,
        filteredValue: columnFilteredValue,
        filters: [],
      };

      if (isExportDateTimeColumn(col)) {
        parsedColumns.push({
          ...baseColumnProps,
          filterDropdown: renderCustomExportDateTimeFilterDropdown(col),
          onFilter: (value, record) => rowMatchesExportDateFilter(record, col, dataKey, value),
        });
      } else if (isExportNumericLikeColumn(col, exportColumnFieldMap)) {
        parsedColumns.push({
          ...baseColumnProps,
          filterDropdown: renderCustomExportNumberFilterDropdown,
          onFilter: (value, record) => {
            if (!value) return false;
            return rowMatchesExportNumberFilter(record[dataKey], value);
          },
        });
      } else {
        const isText = isExportTextOrAttributeColumn(col, exportColumnFieldMap);
        const additionalProps = isText
          ? {
            filterDropdown: renderCustomExportTextFilterDropdown,
          } : {
            filters: columnFilterOptionMap[dataKey] || [],
            filterSearch: true,
            filterDropdown: renderCustomExportGeneralFilterDropdown,
          }

        parsedColumns.push({
          ...baseColumnProps,
          ...additionalProps,
          onFilter: (value, record) => {
            const cellValue = extractTextFromCell(record[dataKey]);
            return rowMatchesExportColCombinedFilter(cellValue, value);
          },
        });
      }
    });

    return parsedColumns;
  }, [
    specialColumns,
    exportColumns,
    exportId,
    columnFilterOptionMap,
    tableFilters,
    exportColumnFieldMap,
    hasSubTotals,
  ]);

  const renderSummary = useCallback(() => (
    <TableV5.Summary fixed>
      <TableV5.Summary.Row>
        {columns.map((col, index) => {
          const cellKey = col.key ?? `subtotal-${index}`;
          const isLabelColumn = col.key === SUBTOTAL_LABEL_KEY;
          const value = isLabelColumn
            ? 'Sub Total'
            : (col.key != null ? subTotalsByKey[col.key] : undefined) ?? '';
          return (
            <TableV5.Summary.Cell index={index} key={cellKey}>
              <div style={{ whiteSpace: 'nowrap', fontWeight: isLabelColumn ? 600 : undefined }}>
                {value}
              </div>
            </TableV5.Summary.Cell>
          );
        })}
      </TableV5.Summary.Row>
    </TableV5.Summary>
  ), [columns, subTotalsByKey]);

  const onCalenderTypeChange = useCallback((newType) => {
    setCalendarType(newType);
    const [endDay] = dateRange;
    const now = DateTime.local();
    const realEnd = now > endDay ? now : endDay;
    let newRange = [...dateRange];
    if (newType === 'payPeriod') {
      newRange = findPayrollStartAndEndDates({
        startDay: realEnd,
        payPeriodFirstDay: firstPayrollDay,
        payPeriod,
        semiMonthlyPayPeriodDates,
      });
    } else if (newType === 'week') {
      newRange = [
        realEnd.startOf('week').minus({ day: 1 }),
        realEnd.endOf('week').minus({ day: 1 }),
      ];
    }
    if (newType !== 'custom') setDateRange(newRange);
  }, [
    dateRange,
    payPeriod,
    firstPayrollDay,
    semiMonthlyPayPeriodDates,
  ]);

  const showTimeSlider = useCallback(() => setShowTimeDetail(true), []);
  const hideTimeSlider = useCallback(() => setShowTimeDetail(false), []);

  const openFilterDrawer = useCallback(() => setShowFilterDrawer(true), []);
  const closeFilterDrawer = useCallback(() => setShowFilterDrawer(false), []);

  const onReload = useCallback(() => setShouldRefresh(true), []);

  const onApplyFilter = useCallback((newFilter) => {
    setFilters(newFilter);
    setShouldRefresh(true);
  }, []);

  const onClick = useCallback(async (row) => {
    // Rows with table values have ids like 36hex-0, 36hex-1
    const [realId] = row.id?.split('-') ?? [];
    if (!realId) return;
    if (formTemplateId) {
      const ourId = row.formId ?? realId;
      setRowLoading(true);
      const passed = dispatch(getFormById(ourId));
      if (passed) setShowFormDetail(true);
      setRowLoading(false);
    } else if (boardId) {
      await dispatch(openCardDrawer({
        selectedCardId: realId,
      }));
    } else if (canClick) {
      const ourId = row.taskId ?? realId;
      await dispatch(TimeTracking.getTimeEntries({ ids: [ourId], userId: row.userId }));
      setSelectedRow({ ...row, id: ourId });
      showTimeSlider();
    }
  }, [formTemplateId, boardId, canClick, showTimeSlider]);

  const onExport = useCallback(async () => {
    if (!selectedExport) return;
    let batchNumber;

    if (generateBatchNumber) {
      batchNumber = await getBatchNumber({ exportId });
      if (isNullOrUndefined(batchNumber)) return;
    }

    const uniqueExportFormats = new Set(exportColumns.filter((col) => col.field === 'exportDate').map((col) => col.filterValue ?? null));
    uniqueExportFormats.add(null);
    const exportDateColumns = Array.from(
      uniqueExportFormats,
    );

    let rowsToExport = visibleRowsForExport;

    const now = DateTime.local();

    if (exportDateColumns.length > 0) {
      rowsToExport = visibleRowsForExport.map((row) => {
        const newRow = { ...row };
        exportDateColumns.forEach((dateFormat) => {
          const dateKey = dateFormat ? `exportDate-${dateFormat}` : 'exportDate';
          const format = dateFormat ?? 'mm/dd/yyyy';
          const existing = newRow[dateKey] ?? '';
          const prefix = existing.length ? `${existing}, ` : '';
          const newDateText = now.toFormat(`${DATE_FORMAT_MAP[format]}  hh:mm a`);
          newRow[dateKey] = `${prefix}${newDateText}`;
        });
        return newRow;
      });
    }

    const exportConfig = {
      addLineNumbers,
      columns: exportColumns,
      data: rowsToExport,
      batchNumber,
    };

    const didExport = exporter.createCustomExport(exportTitle, exportConfig);

    if (didExport) {
      const payload = constructAfterExportPayload({
        type,
        rows: rowsToExport,
        generateFormNumber,
        batchNumber,
      });

      if (Object.keys(payload).length === 0) return;

      const { data: formNumberMap = {} } = await request({
        call: axios.put(`/exports/${exportId}/after`, payload),
        hideSuccessToast: true,
      });

      if (type === 'forms') {
        const updatedRows = updateFormNumbers({ data: rowsToExport, formNumberMap });
        setRows(updatedRows);
      } else if (exportDateColumns.length > 0) {
        setRows(rowsToExport);
      }
    }
  }, [
    selectedExport,
    generateBatchNumber,
    generateFormNumber,
    addLineNumbers,
    exportColumns,
    exportTitle,
    exportId,
    visibleRowsForExport,
    type,
  ]);

  const timeEntries = useMemo(() => {
    if (!selectedRow?.id) return [];
    const userTimeEntries = timeEntryUserMap?.[selectedRow.userId] ?? [];
    const ourEntry = userTimeEntries?.find((entry) => entry.id === selectedRow.id);
    return ourEntry ? [ourEntry] : [];
  }, [timeEntryUserMap, selectedRow]);

  const user = useMemo(() => (
    userMap[selectedRow?.userId] ?? {}
  ), [userMap, selectedRow]);

  const relevantTaskHistory = useMemo(() => {
    if (!selectedRow?.id) return [];
    return timeCardHistory.filter((t) => (
      t.taskId === selectedRow.id
    ));
  }, [timeCardHistory, selectedRow]);

  useEffect(() => {
    if (selectedRow?.userId) {
      dispatch(getTimecardHistory(selectedRow?.userId));
    }
  }, [selectedRow]);

  useEffect(() => {
    // Clear filters if export changes
    setFilters();
    setTableFilters({});
    setFilteredData([]);
  }, [selectedExport]);

  useEffect(() => {
    if (payPeriod && firstPayrollDay) {
      const payPeriodRange = findPayrollStartAndEndDates({
        payPeriodFirstDay: firstPayrollDay,
        payPeriod,
        semiMonthlyPayPeriodDates,
      });
      setDateRange(payPeriodRange);
    }
  }, [payPeriod, semiMonthlyPayPeriodDates]);

  useEffect(() => {
    dispatch(getBoards());
    dispatch(getBoardCardTemplates());
    dispatch(getUserCustomFieldTemplate());
  }, [dispatch]);

  const typeText = type === 'boardCards' ? 'board card' : type;

  return (
    <>
      <ResizeObserver onResize={onHeaderResize}>
        <Row justify="space-between">
          <Col>
            <Row justify="start" gutter={16} align="middle">
              <TimeCardDatePicker
                onChange={setDateRange}
                value={dateRange}
                onTypeChange={onCalenderTypeChange}
                type={calendarType}
              />
              <Col>
                {
                  shouldRefresh
                    ? <Spin size="small" style={{ marginTop: 4, paddingLeft: 8 }} />
                    : (
                      <BorderlessButton
                        iconNode={<ReloadOutlined />}
                        onClick={onReload}
                        style={{ backgroundColor: 'transparent', paddingLeft: 0 }}
                      />
                    )
                }
              </Col>
            </Row>
          </Col>
          <Col>
            <Row justify="end" gutter={4} align="middle">
              {canFilter && (
                <Col style={{ marginRight: 5 }}>
                  <OnTraccrButton
                    title=""
                    icon={
                      filterIsActive
                        ? <FilterFilled style={{ marginLeft: 0 }} />
                        : <FilterOutlined style={{ marginLeft: 0 }} />
                      }
                    onClick={openFilterDrawer}
                  />
                </Col>
              )}
              {type !== 'boardCards' && (
                <Col>
                  <OnTraccrSwitch
                    value={hideExported}
                    onChange={setHideExported}
                    title="Hide previous exports?"
                  />
                </Col>
              )}
              {exportId !== 'preview' && (
              <Col>
                <OnTraccrButton
                  title="Export"
                  onClick={onExport}
                  type="cancel"
                />
              </Col>
              )}
            </Row>
          </Col>
        </Row>
      </ResizeObserver>
      <Divider />
      {
        loading
          ? (
            <Row justify="center" align="middle" style={{ height: '100%', width: '100%' }}>
              <Col>
                <Spin />
              </Col>
            </Row>
          )
          : (
            <>
              {excluded > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 12 }}
                  message={`${excluded} ${typeText}${excluded === 1 ? '' : 's'} ${excluded === 1 ? 'was' : 'were'} excluded due to missing value in the selected date field.`}
                />
              )}
              <TableV5
                virtual
                className="custom-export-table"
                columns={columns}
                dataSource={exportRows}
                pagination={false}
                scroll={{ x: 'fit-content', y: scroll }}
                rowKey="id"
                style={{ overflowX: 'hidden' }}
                rowClassName={canClick ? 'file-table-row' : null}
                onRow={(entry) => ({
                  onClick: canClick ? () => onClick(entry) : null,
                })}
                loading={rowLoading}
                onChange={onTableChange}
                summary={hasSubTotals ? renderSummary : undefined}
              />
            </>
          )
      }
      <FormDetailView
        userMap={userMap}
        visible={showFormDetail}
        onClose={() => setShowFormDetail(false)}
        startOnFields
      />
      <TimeCardSlider
        visible={showTimeDetail}
        onClose={hideTimeSlider}
        user={user}
        initialEntries={timeEntries}
        initialHistory={relevantTaskHistory}
        isDisplay
      />
      <BoardCardDrawer
        boardId={boardId}
        cardTypeId={ourBoard?.cardTypeId}
        divisions={ourBoard?.divisions}
        showCardNumber={ourBoard?.showCardNumber}
        useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
        isDisplay
        getContainer={() => document.body}
      />
      <FilterDrawer
        visible={showFilterDrawer}
        onClose={closeFilterDrawer}
        filterViews={relevantFilterViews}
        CustomFilterTab={CustomExportFilterTab}
        selectedExport={selectedExport}
        onApplyFilter={onApplyFilter}
        activeFilters={filters}
        usePropFilters
      />
    </>
  );
}

CustomExportsTable.propTypes = {
  selectedExport: CUSTOM_EXPORT_PROP_TYPE,
  scroll: PropTypes.string,
  onHeaderResize: PropTypes.func,
  canFilter: PropTypes.bool,
};

CustomExportsTable.defaultProps = {
  selectedExport: null,
  scroll: null,
  onHeaderResize: null,
  canFilter: false,
};

export default CustomExportsTable;
